import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// School Years
export const schoolYearApi = {
  getAll: () => api.get('/school-years'),
  getActive: () => api.get('/school-years/active'),
  create: (data) => api.post('/school-years', data),
  activate: (id) => api.put(`/school-years/${id}/activate`),
};

// Levels
export const levelApi = {
  getAll: (type) => api.get('/levels', { params: { type } }),
  create: (data) => api.post('/levels', data),
};

// Classes
export const classApi = {
  getAll: (params) => api.get('/classes', { params }),
  create: (data) => api.post('/classes', data),
};

// Students
export const studentApi = {
  getAll: (params) => api.get('/students', { params }),
  getById: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
  exportCsv: (params) => api.get('/students/export/csv', { params, responseType: 'blob' }),
  getTemplate: () => api.get('/students/template/csv', { responseType: 'blob' }),
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/students/import/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Teachers
export const teacherApi = {
  getAll: (params) => api.get('/teachers', { params }),
  getById: (id) => api.get(`/teachers/${id}`),
  create: (data) => api.post('/teachers', data),
  update: (id, data) => api.put(`/teachers/${id}`, data),
};

// Surahs
export const surahApi = {
  getAll: () => api.get('/surahs'),
  seed: () => api.post('/surahs/seed'),
};

// Quranic Progress
export const quranicProgressApi = {
  getAll: (params) => api.get('/quranic-progress', { params }),
  create: (data) => api.post('/quranic-progress', data),
  update: (id, data) => api.put(`/quranic-progress/${id}`, data),
};

// Evaluations
export const evaluationApi = {
  getAll: (params) => api.get('/evaluations', { params }),
  create: (data) => api.post('/evaluations', data),
};

// Fees
export const feeApi = {
  getAll: (params) => api.get('/fees', { params }),
  create: (data) => api.post('/fees', data),
};

// Payments
export const paymentApi = {
  getAll: (params) => api.get('/payments', { params }),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Boarding Fees (Tarifs des régimes)
export const boardingFeeApi = {
  getAll: (params) => api.get('/boarding-fees', { params }),
  getById: (id) => api.get(`/boarding-fees/${id}`),
  create: (data) => api.post('/boarding-fees', data),
  update: (id, data) => api.put(`/boarding-fees/${id}`, data),
  delete: (id) => api.delete(`/boarding-fees/${id}`),
  seedDefaults: (schoolYearId) => api.post(`/boarding-fees/seed-defaults?school_year_id=${schoolYearId}`),
};

// Email Notifications
export const emailApi = {
  send: (data) => api.post('/notifications/email', data),
  checkStatus: () => api.get('/notifications/email/status'),
};

// Salary Slips (Bulletins de Salaire)
export const salarySlipApi = {
  getAll: (params) => api.get('/salary-slips', { params }),
  getById: (id) => api.get(`/salary-slips/${id}`),
  create: (data) => api.post('/salary-slips', data),
  update: (id, data) => api.put(`/salary-slips/${id}`, data),
  delete: (id) => api.delete(`/salary-slips/${id}`),
  markPaid: (id) => api.patch(`/salary-slips/${id}/mark-paid`),
  getStats: (month) => api.get('/salary-slips/stats/summary', { params: { month } }),
};

// Expenses (Dépenses)
export const expenseApi = {
  getAll: (params) => api.get('/expenses', { params }),
  getCategories: () => api.get('/expense-categories'),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getDashboard: (params) => api.get('/expenses/stats/dashboard', { params }),
};

// Expense Categories (Catégories de dépenses)
export const expenseCategoryApi = {
  getAll: () => api.get('/expense-categories'),
  getAllIncludingInactive: () => api.get('/expense-categories/all'),
  create: (data) => api.post('/expense-categories', data),
  update: (id, data) => api.put(`/expense-categories/${id}`, data),
  delete: (id) => api.delete(`/expense-categories/${id}`),
  seedDefaults: () => api.post('/expense-categories/seed-defaults'),
};

export default api;
